import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

// Empty string = same origin (production). Local default hits Django runserver.
const API_BASE =
  process.env.REACT_APP_API_URL !== undefined
    ? process.env.REACT_APP_API_URL
    : process.env.NODE_ENV === 'production'
      ? ''
      : 'http://127.0.0.1:8000';

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

export { api };

const clearAuthStorage = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refresh');
  localStorage.removeItem('user');
  delete api.defaults.headers.common['Authorization'];
};

const applyAccessToken = (access) => {
  localStorage.setItem('token', access);
  api.defaults.headers.common['Authorization'] = `Bearer ${access}`;
};

let refreshPromise = null;

const refreshAccessToken = async () => {
  const refresh = localStorage.getItem('refresh');
  if (!refresh) {
    throw new Error('No refresh token');
  }
  // Use bare axios so this call is not caught by our 401 interceptor loop.
  const { data } = await axios.post(`${API_BASE}/api/auth/token/refresh/`, {
    refresh,
  });
  applyAccessToken(data.access);
  if (data.refresh) {
    localStorage.setItem('refresh', data.refresh);
  }
  return data.access;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const setUserRef = useRef(setUser);
  setUserRef.current = setUser;

  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      (response) => response,
      async (err) => {
        const original = err.config;
        const status = err.response?.status;
        const url = original?.url || '';

        const isAuthEndpoint =
          url.includes('/auth/login/') ||
          url.includes('/auth/register/') ||
          url.includes('/auth/token/refresh/');

        if (status !== 401 || !original || original._retry || isAuthEndpoint) {
          return Promise.reject(err);
        }

        original._retry = true;

        try {
          if (!refreshPromise) {
            refreshPromise = refreshAccessToken().finally(() => {
              refreshPromise = null;
            });
          }
          const access = await refreshPromise;
          original.headers = original.headers || {};
          original.headers.Authorization = `Bearer ${access}`;
          return api(original);
        } catch (refreshError) {
          clearAuthStorage();
          setUserRef.current(null);
          return Promise.reject(refreshError);
        }
      }
    );

    return () => {
      api.interceptors.response.eject(interceptor);
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        applyAccessToken(token);
      } catch (parseError) {
        console.error('Error parsing user data:', parseError);
        clearAuthStorage();
      }
    }
    setLoading(false);
  }, []);

  const login = async (username, password) => {
    try {
      const response = await api.post('/api/auth/login/', {
        username,
        password,
      });

      const { access, refresh, user: loggedInUser } = response.data;
      applyAccessToken(access);
      localStorage.setItem('refresh', refresh);
      localStorage.setItem('user', JSON.stringify(loggedInUser));
      setUser(loggedInUser);
      setError(null);
      return true;
    } catch (loginError) {
      console.error('Login error:', loginError);
      setError(
        loginError.response?.data?.error ||
          loginError.response?.data?.detail ||
          'Login failed'
      );
      return false;
    }
  };

  const register = async (username, email, password) => {
    try {
      const response = await api.post('/api/auth/register/', {
        username,
        email,
        password,
      });

      const { access, refresh, user: registeredUser } = response.data;
      applyAccessToken(access);
      localStorage.setItem('refresh', refresh);
      localStorage.setItem('user', JSON.stringify(registeredUser));
      setUser(registeredUser);
      setError(null);
      return true;
    } catch (registerError) {
      console.error('Registration error:', registerError);
      setError(registerError.response?.data?.detail || 'Registration failed');
      return false;
    }
  };

  const logout = () => {
    clearAuthStorage();
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        login,
        register,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
